import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useApi, configApiRef } from '@backstage/core-plugin-api';

type Status = 'checking' | 'available' | 'unavailable';

/**
 * BackendRequiredPage — バックエンド接続が必要なページのガード。
 *
 * Backstage バックエンド（packages/backend）が起動していない場合、
 * 子コンポーネントをレンダリングせずフォールバックUIを表示する。
 * これにより CatalogClient 等のAPI呼び出しエラーが AlertDisplay に
 * 漏れることを防ぐ。
 */
export function BackendRequiredPage({
  children,
  feature = 'この機能',
}: {
  children: React.ReactNode;
  feature?: string;
}) {
  const config = useApi(configApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  const [status, setStatus] = useState<Status>('checking');

  const checkBackend = async () => {
    setStatus('checking');
    try {
      const res = await fetch(`${backendUrl}/api/catalog/entities?limit=0`, {
        method: 'GET',
        signal: AbortSignal.timeout(3000),
      });
      setStatus(res.ok ? 'available' : 'unavailable');
    } catch {
      setStatus('unavailable');
    }
  };

  useEffect(() => {
    checkBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUrl]);

  if (status === 'checking') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="40vh"
      >
        <Typography variant="body1" color="textSecondary">
          バックエンドに接続中...
        </Typography>
      </Box>
    );
  }

  if (status === 'unavailable') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        p={4}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 520,
            p: 5,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CloudOffIcon
            sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }}
          />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            バックエンド未接続
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{ mb: 3, lineHeight: 1.7 }}
          >
            {feature}を利用するには Backstage バックエンドの起動が必要です。
            <br />
            <code style={{ fontSize: '0.85em' }}>yarn dev</code>{' '}
            でフロント・バックエンド同時起動するか、バックエンドを個別に起動してください。
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={checkBackend}
          >
            再チェック
          </Button>
        </Paper>
      </Box>
    );
  }

  return <>{children}</>;
}
